import { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Sparkles, Trophy, ArrowDown, PartyPopper, HeartHandshake } from "lucide-react";
import { ASSETS } from "./data";

const lineReveal = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.15 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
  }),
};

const MaskedLine = ({ children, i, className }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span custom={i} variants={lineReveal} className={`block ${className || ""}`}>
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const [isDesktop, setIsDesktop] = useState(
    () => window.matchMedia("(min-width: 1024px)").matches
  );
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const plushieY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const plushieRotate = useTransform(scrollYProgress, [0, 1], [0, -6]);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const onChange = (e) => setIsDesktop(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative overflow-hidden grid-bg py-14 lg:py-20"
    >
      <div className="neon-hairline absolute top-0 left-0 right-0" />
      <div className="absolute -top-24 -right-24 w-[500px] h-[500px] rounded-full bg-cyan-300/20 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 -left-32 w-[420px] h-[420px] rounded-full bg-pink-400/15 blur-[120px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center relative z-10 w-full">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="lg:col-span-7">
          <MaskedLine i={0}>
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.3em] text-pink-500">
              <Sparkles size={14} /> Ashley Falls PTA · 2026–27 Membership Drive
            </span>
          </MaskedLine>

          <h1
            data-testid="hero-headline"
            className="mt-6 font-black uppercase tracking-tighter leading-[0.95] text-[2.4rem] sm:text-5xl lg:text-6xl text-slate-900"
          >
            <MaskedLine i={1} className="neon-cyan-shadow">PTA is</MaskedLine>
            <MaskedLine i={2} className="neon-cyan-shadow">Soaring to</MaskedLine>
            <MaskedLine i={3} className="text-transparent bg-clip-text bg-gradient-to-r from-pink-500 via-fuchsia-500 to-cyan-500">
              Success!
            </MaskedLine>
          </h1>

          <MaskedLine i={4}>
            <p data-testid="hero-subheadline" className="mt-2 max-w-xl text-base md:text-lg font-medium leading-relaxed" style={{ color: "#0A192F" }}>
              Join the Ashley Falls PTA today to fund our exceptional campus
              programs and support our STEAM+ teachers —{" "}
              <strong>your membership matters!</strong>
            </p>
          </MaskedLine>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <a
              href="#tiers"
              data-testid="hero-cta-button"
              className="w-full sm:w-auto justify-center px-8 py-4 rounded-full bg-slate-900 text-cyan-300 font-bold uppercase tracking-widest text-sm hover:shadow-[0_0_25px_rgba(0,240,255,0.5)] transition-shadow inline-flex items-center gap-2"
            >
              Pick your tier <ArrowDown size={16} />
            </a>
            <div
              data-testid="hero-trust-badge"
              className="inline-flex items-center gap-2 bg-cyan-50 border border-cyan-200 text-slate-800 px-4 py-3 rounded-full text-xs sm:text-sm font-semibold shadow-sm max-w-md"
            >
              <Trophy size={16} className="text-pink-500 shrink-0" />
              <span>
                Last year, your PTA memberships funded over{" "}
                <strong>$100,000</strong> in student programs, classroom grants,
                and staff appreciation!
              </span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.7 }}
            data-testid="hero-support-banner"
            className="mt-8 inline-flex items-center gap-3 bg-white/70 backdrop-blur-sm border border-pink-200 rounded-2xl px-5 py-4 shadow-[0_0_25px_rgba(255,0,127,0.12)]"
          >
            <HeartHandshake size={20} className="text-pink-500 shrink-0" />
            <span className="text-xs sm:text-sm font-black uppercase tracking-[0.15em] text-slate-900">
              The drive continues —{" "}
              <span className="text-pink-500">join today, your support matters!</span>
            </span>
          </motion.div>
        </motion.div>

        <div className="lg:col-span-5 relative flex justify-center pb-10 lg:pb-0">
          <motion.div
            style={isDesktop ? { y: plushieY, rotate: plushieRotate } : undefined}
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="absolute inset-6 rounded-full bg-gradient-to-br from-cyan-300/40 via-pink-300/30 to-yellow-200/40 blur-3xl" />
            <motion.img
              src={ASSETS.plushie}
              alt="3-Foot Eagle Plushie — congrats to grand prize winner Allen Lou"
              data-testid="hero-plushie-image"
              className="relative w-full max-w-[92vw] sm:max-w-md lg:max-w-sm drop-shadow-2xl"
              animate={{ y: [0, -14, 0] }}
              transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.6, rotate: -8 }}
              animate={{ opacity: 1, scale: 1, rotate: -8 }}
              transition={{ delay: 1.2, duration: 0.5 }}
              data-testid="hero-prize-tag"
              className="absolute -bottom-2 left-2 sm:left-6 bg-slate-900 text-white px-4 py-2.5 sm:px-5 sm:py-3 rounded-2xl border border-pink-500 shadow-[0_0_25px_rgba(255,0,127,0.4)]"
            >
              <p className="text-[10px] uppercase tracking-[0.25em] text-pink-400 font-bold inline-flex items-center gap-1.5">
                <PartyPopper size={12} /> Grand Prize Winner
              </p>
              <p className="font-black text-base sm:text-lg leading-tight">Congrats, Allen Lou!</p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
